import {
  Conversation,
  ConversationEvent,
} from '@stripe-internal/conversations-client';
import {filter, Subscription, startWith, map, distinctUntilChanged} from 'rxjs';
import {useEffect, useState} from 'react';
import {
  useWidgetNotifications,
  useWidgetVisibility,
  Visibility,
  SoundSetting,
} from '@stripe-internal/conversations-ui';
import {useDocumentVisibility} from '../../useDocumentVisibility';
import {useProfile} from '../../Providers/ProfileProvider';

const isNonUserMessageEvent = (
  event: ConversationEvent,
  conversation: Conversation,
) => {
  return (
    event.name === 'conversation-message-added' &&
    event.participantId !== conversation.user.id
  );
};

const isAgentMessageEvent = (
  event: ConversationEvent,
  conversation: Conversation,
) => {
  return (
    event.name === 'conversation-message-added' &&
    Object.values(conversation.participants).some(
      (participant) =>
        participant.id === event.participantId && participant.role === 'agent',
    )
  );
};

const widgetNotOpen = (
  visibility: Visibility,
  documentVisibility: DocumentVisibilityState,
): boolean => {
  return visibility !== 'open' || documentVisibility === 'hidden';
};

const shouldIncreaseUnseenMessagesCount = (
  visibility: Visibility,
  documentVisibility: DocumentVisibilityState,
): boolean => {
  return widgetNotOpen(visibility, documentVisibility);
};

const shouldPlaySound = (conversation: Conversation): boolean => {
  return conversation.isTabMostRecentlyInteracted();
};

const setContextValueIfChanged = (
  conversation: Conversation,
  key: string,
  value: any,
) => {
  if (conversation.context[key] !== value) {
    conversation.setContextValue(key, value);
  }
};

export const useConversationNotifications = (conversation: Conversation) => {
  const {
    playSound,
    setUnseenMessagesCount,
    soundSetting,
    setSoundSetting,
    soundSettingObservable,
  } = useWidgetNotifications();
  const {visibility} = useWidgetVisibility();
  const {documentVisibility} = useDocumentVisibility();
  const {profile} = useProfile();
  const {featureFlags} = profile;

  const [unseenCount, setUnseenCount] = useState<number>(0);

  // [Visual] Update unseen messages count accordingly
  useEffect(() => {
    setUnseenMessagesCount(unseenCount);
  }, [setUnseenMessagesCount, unseenCount]);

  useEffect(() => {
    if (unseenCount && !widgetNotOpen(visibility, documentVisibility)) {
      setUnseenCount(0);
    }
  }, [unseenCount, visibility, documentVisibility]);

  // [Sound] [Visual] Filter through conversation events and perform a visual or sound notification on applicable ones
  useEffect(() => {
    const sub = new Subscription();

    // Filter out notifiable events
    const visuallyNotifiableEvents$ = conversation
      .eventsObservable()
      .pipe(filter((event) => isNonUserMessageEvent(event, conversation)));

    const soundNotifiableEvents$ = conversation
      .eventsObservable()
      .pipe(filter((event) => isAgentMessageEvent(event, conversation)));

    // Further determine if it's the right circumstance notify
    sub.add(
      visuallyNotifiableEvents$.subscribe((_) => {
        if (shouldIncreaseUnseenMessagesCount(visibility, documentVisibility)) {
          setUnseenCount((prevCount) => prevCount + 1);
        }
      }),
    );

    sub.add(
      soundNotifiableEvents$.subscribe(async (_) => {
        if (shouldPlaySound(conversation)) {
          const soundPlayed = await playSound('new-message');
          conversation.client.analytics.action(
            'sound_notification_triggered_on_new_message',
            {
              soundPlayed,
            },
          );
        }
      }),
    );
    return () => sub.unsubscribe();
  }, [conversation, documentVisibility, visibility, playSound, soundSetting]);

  // [Sound] Enable/Disable sound setting if applicable. This hook only sets the source of truth, the context value;
  // the 2-way binding hooks below will take care of syncing.
  useEffect(() => {
    const sub = conversation
      .contextObservable()
      // Force emit the current context immediately.
      .pipe(startWith(conversation.context))
      .subscribe(() => {
        // using the *current* context as opposed to the context that was emitted from a change
        // this is because that might already be out of date since anything that changes context
        // could change this
        const {context} = conversation;

        // The additional isActive check here (despite a dedicated status observable) is to prevent race conditions where
        // the logic in this observable fires at the same time as that dedicated one.
        if (context.channel !== 'chat' || !conversation.isActive) {
          return;
        }

        const currentSoundSetting = (context.soundSetting ??
          'disabled') as SoundSetting;
        if (['on', 'off'].includes(currentSoundSetting)) {
          // sound has already been enabled
          return;
        }

        // Every conversation starts with the soundSetting defaulting to on, regardless of previous settings
        setContextValueIfChanged(conversation, 'soundSetting', 'on');
      });

    return () => sub.unsubscribe();
  }, [conversation, featureFlags]);

  // [Sound] [2-way binding] Conversation context's soundSetting value changed! --- affects ---> the UI sound setting value to match
  useEffect(() => {
    const sub = conversation
      .contextObservable()
      .pipe(
        // We only care about the soundSetting value part of the context
        map((context) => context.soundSetting),
        // Force it to emit the current soundSetting value immediately, instead of waiting on a change
        startWith(conversation.context.soundSetting),
        // Only emit changes
        distinctUntilChanged(),
      )
      .subscribe(() => {
        // use the *current* context as opposed to the context that was emitted from a change
        const newSoundSetting = conversation.context.soundSetting;
        if (newSoundSetting) {
          // the UI sound setting value can not be undefined even though conversation context's can.
          // And this helps us avoid race conditions where an undefined and actual value is fired at the same time
          setSoundSetting(newSoundSetting as SoundSetting);
        }
      });

    return () => sub.unsubscribe();
  }, [conversation, setSoundSetting]);

  // [Sound] [2-way binding] UI value changed! (ex. speaker button toggled) --- affects ---> conversation context to match
  useEffect(() => {
    const sub = soundSettingObservable().subscribe(
      (newSoundSetting: SoundSetting) => {
        setContextValueIfChanged(conversation, 'soundSetting', newSoundSetting);
      },
    );
    return () => sub.unsubscribe();
  }, [conversation, soundSettingObservable]);

  // [Sound] immediately disable sound setting when conversation becomes inactive
  useEffect(() => {
    const sub = conversation.statusObservable().subscribe((_) => {
      if (!conversation.isActive) {
        setContextValueIfChanged(conversation, 'soundSetting', 'disabled');
      }
    });
    return () => sub.unsubscribe();
  }, [conversation]);
};
